import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  SITE_URL, SITE_NAME, OG_IMAGE, DEFAULT_DESCRIPTION, DEFAULT_KEYWORDS, TWITTER_HANDLE,
} from "@/lib/seo";

const ATTR = "data-seo"; // marks tags we manage so we can clean them up

function setMetaByName(name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"][${ATTR}]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    el.setAttribute(ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaByProp(prop, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[property="${prop}"][${ATTR}]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    el.setAttribute(ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector(`link[rel="canonical"][${ATTR}]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute(ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * SEO head manager (dependency-free, React 19 compatible).
 * Props: title, description, keywords, image, type, noindex, schema (object or array of JSON-LD).
 */
export const Seo = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  image = OG_IMAGE,
  type = "website",
  noindex = false,
  schema,
}) => {
  const { pathname } = useLocation();
  const canonical = `${SITE_URL}${pathname === "/" ? "" : pathname}`;
  const fullTitle = title || `${SITE_NAME} — Premier AI Legal Research, Drafting & Judiciary Prep`;

  useEffect(() => {
    document.title = fullTitle;
    setMetaByName("description", description);
    setMetaByName("keywords", keywords);
    setMetaByName("robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    setCanonical(canonical);

    // Open Graph
    setMetaByProp("og:title", fullTitle);
    setMetaByProp("og:description", description);
    setMetaByProp("og:type", type);
    setMetaByProp("og:url", canonical);
    setMetaByProp("og:image", image);
    setMetaByProp("og:site_name", SITE_NAME);
    setMetaByProp("og:locale", "en_IN");

    // Twitter
    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", fullTitle);
    setMetaByName("twitter:description", description);
    setMetaByName("twitter:image", image);
    setMetaByName("twitter:site", TWITTER_HANDLE);

    // JSON-LD structured data
    const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
    const scriptEls = schemas.map((s) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute(ATTR, "true");
      script.text = JSON.stringify(s);
      document.head.appendChild(script);
      return script;
    });

    return () => {
      scriptEls.forEach((s) => s.remove());
    };
  }, [fullTitle, description, keywords, canonical, image, type, noindex, schema]);

  return null;
};

export default Seo;
